import { useEffect, useState } from "react";
import { Box, Checkbox, ListItemText, ListSubheader, InputAdornment } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { Controller, useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { ReactComponent as DeleteBinIcon } from "../../assets/icons/delete-bin.svg";
import errorIcon from "../../assets/icons/error-icon.svg";
import { useGetValueDropdown } from "../../services/segment";
import { useCountryList } from "../../services/okta";
import { CircularProgressCustom, CustomChip } from "../../utils/muiCustomTheme";
import DateField from "../../shared/components/DateField";
import CustomTooltip from "../../shared/components/custom-tooltip/CustomTooltip";

const deleteRowTooltip = {
  alt: "delete condition",
  tooltip: "Delete condition",
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      width: 250,
    },
  },
  MenuListProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      overflowY: "auto" as const,
      paddingTop: 0,
    },
  },
};

type FilterWrapSegmentSectionProps = {
  type?: string;
  listFilter?: any;
  table?: any;
  field?: any;
  indexSection?: any;
  removeDelete?: any;
  controlName?: any;
  parentPath?: any;
  handleUpdateFilterCondition?: any;
  indexCondition?: any;
  selectOptions?: string[];
  onRemove?: () => void;
};

const FilterWrapSegmentSection = ({
  type,
  selectOptions = [],
  onRemove,
  listFilter,
  indexSection,
  indexCondition,
  handleUpdateFilterCondition,
  parentPath,
  controlName,
  removeDelete = false,
  table,
  field: fieldKey,
}: FilterWrapSegmentSectionProps) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const currentPathSplit = currentPath.split("/");
  const methods = useFormContext();
  const { control, setValue, getValues, watch, setError, clearErrors, trigger } = methods;
  const [searchText, setSearchText] = useState("");

  const { data: countries } = useCountryList();

  const countryTags = Array.from(new Set(countries?.flatMap((c) => c.tags) || []));

  const sectionConditions = watch(`${controlName}.${indexSection}.conditions`) || [];
  const surveyQuestionCondition = sectionConditions.find(
    (c: any, i: number) => i !== indexCondition && c?.table === table && c?.field === "question"
  );
  const selectedSurveyQuestion = Array.isArray(surveyQuestionCondition?.value)
    ? surveyQuestionCondition?.value?.[0]
    : surveyQuestionCondition?.value;

  const isSurveyAnswer =
    typeof table === "string" && table.includes("entity_survey_table") && fieldKey === "answer";
  const effectiveType = isSurveyAnswer ? "select" : type;

  const {
    data: dataValueDropdown,
    isPending: isPendingDataValueDropdown,
    isFetching: isFetchingDataValueDropdown,
  } = useGetValueDropdown({
    query: {
      table_name: table,
      country: watch("country"),
      tags: countryTags,
      ...(isSurveyAnswer && selectedSurveyQuestion ? { question: selectedSurveyQuestion } : {}),
    },
    options: {
      enabled:
        (effectiveType === "select" || effectiveType === "select2") &&
        selectOptions?.length === 0 &&
        countryTags.length > 0 &&
        (!isSurveyAnswer || Boolean(selectedSurveyQuestion)),
    },
  });

  const parseDataValueDropdown = JSON.parse(dataValueDropdown?.data || null);

  const dateAndDateTime = [
    { label: "date is on", value: "on" },
    { label: "date is not on", value: "not on" },
    { label: "before date", value: "before" },
    { label: "after date", value: "after" },
    { label: "between dates", value: "between" },
    { label: "not between dates", value: "not between" },
    { label: "in the past", value: "in last" },
    { label: "in the next", value: "in next" },
  ];

  const typeOptionDropdownList: any = {
    number: [
      { label: "more than", value: ">" },
      { label: "less than", value: "<" },
      { label: "exactly", value: "=" },
      { label: "at least", value: ">=" },
      { label: "at most", value: "<=" },
      { label: "between", value: "number between" },
      { label: "not between", value: "number not between" },
    ],
    date: dateAndDateTime,
    date_time: dateAndDateTime,
    select: [
      { label: "is exactly", value: "=" },
      { label: "in the list", value: "in" },
      { label: "not in the list", value: "not in" },
      { label: "is not exactly", value: "<>" },
    ],
    select2: [
      { label: "is", value: "=" }
    ],
    text: [
      { label: "contains", value: "contains" },
      { label: "not contains", value: "not contains" },
      { label: "is exactly", value: "text is exactly" },
      { label: "is not exactly", value: "text is not exactly" },
      { label: "starts with", value: "starts with" },
      { label: "ends with", value: "ends with" },
      { label: "is empty", value: "is empty" },
      { label: "is not empty", value: "is not empty" },
    ],
  };

  const tempDataValueDropdownValue = listFilter?.find(
    (item: any) =>
      item?.label ===
      watch(`${controlName}.${indexSection}.conditions.${indexCondition}.selectedValue`)
  )?.value;

  useEffect(() => {
    if (!isSurveyAnswer) return;
    const op = watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`);
    const isMulti = op === "in" || op === "not in";
    setValue(
      `${controlName}.${indexSection}.conditions.${indexCondition}.value`,
      isMulti ? [] : ""
    );
    clearErrors(`${controlName}.${indexSection}.conditions.${indexCondition}.value`);
  }, [
    isSurveyAnswer,
    selectedSurveyQuestion,
    clearErrors,
    controlName,
    indexSection,
    indexCondition,
    setValue,
    watch,
  ]);

  const isShowFieldValueText =
    effectiveType === "text" &&
    watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`) &&
    watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`) !== "is empty" &&
    watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`) !==
      "is not empty";
  return (
    <>
      <div className="dropdown__parent-display">
        {Array.isArray(parentPath)
          ? parentPath.map(p => typeof p === "string" ? p : p.label).join(" → ")
          : parentPath}
      </div>
      <div className="dropdown__filter-wrap form-control-container">
        <div className="dropdown__filter-wrap__action-fields">
          <div className="dropdown__filter-wrap__action-fields__wrap">
            {onRemove && !removeDelete && (
              <div
                className="dropdown__filter-wrap__action"
                onClick={onRemove}
                style={{ flexShrink: 0 }}
              >
                {/* Delete */}
                {deleteRowTooltip && (
                  <CustomTooltip title={deleteRowTooltip.tooltip}>
                    <DeleteBinIcon
                      className="deleteRow__icon"
                      role="img"
                      aria-label={deleteRowTooltip.alt}
                    />
                  </CustomTooltip>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="dropdown__filter-wrap__container">
          <div className="dropdown__filter-wrap__fields">
            <div className="dropdown__filter-wrap__fields__filter">
              <Controller
                control={control}
                name={`${controlName}.${indexSection}.conditions.${indexCondition}.selectedValue`}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <>
                      <label className="form-control-container__label">
                        Filter
                        <Select
                          {...field}
                          disabled={
                            currentPathSplit?.[1] === "segments"
                              ? true
                              : isPendingDataValueDropdown && isFetchingDataValueDropdown
                          }
                          onChange={(e) => {
                            const selectedOption = e.target.value;
                            const currentPath = [...parentPath, selectedOption];

                            field.onChange(e.target.value);

                            const ojb = {
                              isUpdate: true,
                              indexCondition,
                            };
                            handleUpdateFilterCondition(currentPath, ojb);
                          }}
                          value={field.value ?? ""}
                          className="form-control-container__select"
                        >
                          {listFilter?.map((option: any, index: number) => (
                            <MenuItem key={index} value={option.label}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </label>

                      {error?.message && (
                        <div className="create-segment__input-wrap__segment-err">
                          <img src={errorIcon} alt="Error Icon" />
                          {error?.message}
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>

            <div className="dropdown__filter-wrap__fields__filter">
              <Controller
                control={control}
                rules={{
                  required: "required",
                }}
                name={`${controlName}.${indexSection}.conditions.${indexCondition}.operator`}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <>
                      <label className="form-control-container__label">
                        Operator
                        <Select
                          {...field}
                          disabled={
                            currentPathSplit?.[1] === "segments"
                              ? true
                              : isPendingDataValueDropdown && isFetchingDataValueDropdown
                          }
                          onChange={(e) => {
                            const newOperator = e.target.value;
                            if (effectiveType === "select") {
                              if (newOperator === "in" || newOperator === "not in") {
                                setValue(
                                  `${controlName}.${indexSection}.conditions.${indexCondition}.value`,
                                  []
                                );
                              } else {
                                setValue(
                                  `${controlName}.${indexSection}.conditions.${indexCondition}.value`,
                                  ""
                                );
                              }
                            } else {
                              setValue(
                                `${controlName}.${indexSection}.conditions.${indexCondition}.value`,
                                ""
                              );
                            }

                            field.onChange(newOperator);
                          }}
                          value={field.value ?? ""}
                          className="form-control-container__select"
                        >
                          {typeOptionDropdownList?.[effectiveType || "number"]?.map(
                            (option: any, index: number) => (
                              <MenuItem key={index} value={option.value}>
                                {option.label}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </label>

                      {error?.message && (
                        <div className="create-segment__input-wrap__segment-err">
                          <img src={errorIcon} alt="Error Icon" />
                          {error?.message}
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>

            {isPendingDataValueDropdown && isFetchingDataValueDropdown && <CircularProgressCustom />}

            {isShowFieldValueText && (
              <div className="dropdown__filter-wrap__fields__filter">
                <Controller
                  control={control}
                  rules={{
                    validate: (val) => (val?.includes("%") ? "Character % is not allowed" : true),
                    required: "required",
                  }}
                  name={`${controlName}.${indexSection}.conditions.${indexCondition}.value`}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <>
                        <label ref={field.ref} className="form-control-container__label">
                          Value
                          <TextField
                              {...field}
                              disabled={
                                currentPathSplit?.[1] === "segments"
                                  ? true
                                  : isPendingDataValueDropdown && isFetchingDataValueDropdown
                              }
                              value={field.value ?? ""}
                              fullWidth
                            multiline
                            onChange={(e) => {
                              field.onChange(e.target.value);
                            }}
                            variant="outlined"
                          />
                        </label>

                        {error?.message && (
                          <div className="create-segment__input-wrap__segment-err">
                            <img src={errorIcon} alt="Error Icon" />
                            {error?.message}
                          </div>
                        )}
                      </>
                    );
                  }}
                />
              </div>
            )}

            {(effectiveType === "select" || effectiveType === "select2" || effectiveType === "boolean") &&
              watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`) && (
                <div className="dropdown__filter-wrap__fields__filter">
                  <Controller
                    control={control}
                    rules={{
                      required: "required",
                    }}
                    name={`${controlName}.${indexSection}.conditions.${indexCondition}.value`}
                    render={({ field, fieldState: { error } }) => {
                      const isIn =
                        getValues(
                          `${controlName}.${indexSection}.conditions.${indexCondition}.operator`
                        ) === "in";
                        const isNotIn =
                        getValues(
                          `${controlName}.${indexSection}.conditions.${indexCondition}.operator`
                        ) === "not in";

                      return (
                        <>
                          <label className="form-control-container__label">
                            Value
                            <Select
                              multiple={isIn||isNotIn}
                              {...field}
                              disabled={
                                currentPathSplit?.[1] === "segments"
                                  ? true
                                  : (isPendingDataValueDropdown && isFetchingDataValueDropdown) ||
                                    (isSurveyAnswer && !selectedSurveyQuestion)
                              }
                              onChange={(event) => {
                                const {
                                  target: { value },
                                } = event;

                                if (isIn||isNotIn) {
                                  const input = typeof value === "string" ? value?.split(",") : value;

                                  field.onChange(Array.isArray(input) ? input : []);
                                } else {
                                  field.onChange(value);
                                }
                              }}
                              renderValue={(selected) => {
                                const values = Array.isArray(selected) ? selected : [];
                                if (isIn||isNotIn) {
                                  return (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                      {values?.map((value: any, index: any) => {
                                        if (index > 3) {
                                          return null;
                                        }
                                        return (
                                          <CustomChip
                                            key={value}
                                            label={value}
                                            variant="outlined"
                                            onMouseDown={(event: any) => {
                                              event.stopPropagation();
                                            }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  );
                                } else {
                                  return selected ?? "";
                                }
                              }}
                              MenuProps={MenuProps}
                              onOpen={() => setSearchText("")}
                            >
                              <ListSubheader sx={{ backgroundColor: "#fff", position: "sticky", top: 0, zIndex: 1 }}>
                                <TextField
                                  size="small"
                                  autoFocus
                                  placeholder="Search..."
                                  fullWidth
                                  value={searchText}
                                  onChange={(e) => setSearchText(e.target.value)}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  sx={{ my: 0.5 }}
                                />
                              </ListSubheader>
                              {
                              (() => {
                                const optionsSource = selectOptions.length > 0
                                ? selectOptions
                                : parseDataValueDropdown?.[0]?.[tempDataValueDropdownValue];

                                try {
                                  return (optionsSource || [])
                                    ?.filter((filtering: any) => filtering !== "")
                                    ?.filter((option: any) => {
                                      const label = typeof option === "string" ? option : String(option);
                                      return label.toLowerCase().includes(searchText.toLowerCase());
                                    })
                                    ?.map((option: any) =>
                                      typeof option === "string" ? (
                                        <MenuItem key={option} value={option}>
                                          {isIn || isNotIn ? (
                                            <>
                                              <Checkbox
                                                disabled
                                                checked={field.value?.includes(option)}
                                              />
                                              <ListItemText primary={option} />
                                            </>
                                          ) : (
                                            option
                                          )}
                                        </MenuItem>
                                      ) : (
                                        <MenuItem key={option} value={option.toString()}>
                                          {option}
                                        </MenuItem>
                                      )
                                    );
                                } catch (err) {
                                  console.error("Error rendering options:", err);
                                  return <MenuItem disabled>No options available</MenuItem>;
                                }
                              })()}
                            </Select>
                          </label>

                          {error?.message && (
                            <div className="create-segment__input-wrap__segment-err">
                              <img src={errorIcon} alt="Error Icon" />
                              {error?.message}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </div>
              )}

            {type === "number" &&
              watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`) &&
              !["number between", "number not between"].includes(
                watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`)
              ) && (
                <div className="dropdown__filter-wrap__fields__filter" style={{ width: "150px" }}>
                  <Controller
                    control={control}
                    rules={{
                      required: "required",
                    }}
                    name={`${controlName}.${indexSection}.conditions.${indexCondition}.value`}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <>
                          <label className="form-control-container__label">
                            Value
                            <TextField
                                {...field}
                                disabled={
                                  currentPathSplit?.[1] === "segments"
                                    ? true
                                    : isPendingDataValueDropdown && isFetchingDataValueDropdown
                                }
                                autoComplete="off"
                                type="number"
                              onChange={(e) => {
                                let tempVal;
                                const val = parseFloat(e.target.value);

                                if (!isNaN(val) && val >= 0) {
                                  tempVal = val;
                                } else if (e.target.value === "") {
                                  tempVal = "";
                                }

                                field.onChange(tempVal);
                              }}
                              value={field.value ?? ""}
                              slotProps={{
                                input: {
                                  inputProps: {
                                    min: 0,
                                    step: 0.01,
                                  },
                                },
                              }}
                              sx={{
                                width: 100,
                                height: 40,
                                "& .MuiOutlinedInput-root": {
                                  height: 40,
                                  borderRadius: 28,
                                },
                                "& input": {
                                  padding: "8px 12px",
                                  fontSize: 12,
                                  color: "#434343",
                                  fontWeight: 400,
                                  "&::placeholder": {
                                    color: "#434343",
                                    fontWeight: 400,
                                    opacity: 1,
                                  },
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#767676",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#E20074",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#E20074",
                                },
                              }}
                            />
                          </label>

                          {error?.message && (
                            <div className="create-segment__input-wrap__segment-err">
                              <img src={errorIcon} alt="Error Icon" />
                              {error?.message}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </div>
              )}

            {type === "number" &&
              ["number between", "number not between"].includes(
                watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`)
              ) && (
                <div style={{ display: "flex", gap: "12px" }}>

                  {(() => {
                    const lowerPath = `${controlName}.${indexSection}.conditions.${indexCondition}.value.0`;
                    const upperPath = `${controlName}.${indexSection}.conditions.${indexCondition}.value.1`;

                    return (
                      <>
                        <Controller
                          name={lowerPath}
                          control={control}
                          rules={{
                            required: "Please enter the lower limit.",
                            validate: (lower) => {
                              const upper = getValues(upperPath);

                              if (lower === "" || lower === undefined)
                                return "Please enter the lower limit.";

                              if (upper === "" || upper === undefined) return true;

                              return (
                                Number(lower) < Number(upper) ||
                                "Lower limit must be less than upper limit."
                              );
                            },
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <div
                              className="form-control-container"
                              style={{ minWidth: "150px" }}
                            >
                              <label className="form-control-container__label">
                                Lower Limit
                              </label>

                              <TextField
                                type="number"
                                disabled={
                                  currentPathSplit?.[1] === "segments"
                                    ? true
                                    : isPendingDataValueDropdown && isFetchingDataValueDropdown
                                }
                                value={field.value ?? ""}
                                onChange={(e) => {
                                  const val =
                                    e.target.value === "" ? "" : Number(e.target.value);

                                  field.onChange(val, { shouldValidate: true });

                                  // Revalidate both fields
                                  trigger([lowerPath, upperPath]);
                                }}
                                autoComplete="off"
                              />

                              {error && (
                                <div className="create-segment__input-wrap__segment-err">
                                  <img src={errorIcon} alt="Error" />
                                  {error.message}
                                </div>
                              )}
                            </div>
                          )}
                        />

                        <Controller
                          name={upperPath}
                          control={control}
                          rules={{
                            required: "Please enter the upper limit.",
                            validate: (upper) => {
                              const lower = getValues(lowerPath);

                              if (upper === "" || upper === undefined)
                                return "Please enter the upper limit.";

                              return (
                                Number(upper) > Number(lower) ||
                                "Upper limit must be greater than lower limit."
                              );
                            },
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <div
                              className="form-control-container"
                              style={{ minWidth: "150px" }}
                            >
                              <label className="form-control-container__label">
                                Upper Limit
                              </label>

                              <TextField
                                type="number"
                                disabled={
                                  currentPathSplit?.[1] === "segments"
                                    ? true
                                    : isPendingDataValueDropdown && isFetchingDataValueDropdown
                                }
                                value={field.value ?? ""}
                                onChange={(e) => {
                                  const val =
                                    e.target.value === "" ? "" : Number(e.target.value);

                                  field.onChange(val, { shouldValidate: true });

                                  // Revalidate both fields
                                  trigger([lowerPath, upperPath]);
                                }}
                                autoComplete="off"
                              />

                              {error && (
                                <div className="create-segment__input-wrap__segment-err">
                                  <img src={errorIcon} alt="Error" />
                                  {error.message}
                                </div>
                              )}
                            </div>
                          )}
                        />
                      </>
                    );
                  })()}
                </div>
              )}

            {["on", "not on", "before", "after"]?.includes(
              watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`)
            ) && (
              <div className="dropdown__filter-wrap__fields__filter dropdown__filter-wrap__fields__calendar">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    control={control}
                    rules={{
                      required: "required",
                    }}
                    name={`${controlName}.${indexSection}.conditions.${indexCondition}.value`}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <>
                          <label className="form-control-container__label">
                            Date
                            <DateField
                              disabled={
                                currentPathSplit?.[1] === "segments"
                                  ? true
                                  : isPendingDataValueDropdown && isFetchingDataValueDropdown
                              }
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(e) => field.onChange(dayjs(e).format("YYYY-MM-DD"))}
                              placeholder="Date"
                            />
                          </label>

                          {error?.message && (
                            <div className="create-segment__input-wrap__segment-err">
                              <img src={errorIcon} alt="Error Icon" />
                              {error?.message}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </LocalizationProvider>
              </div>
            )}

            {["between", "not between"]?.includes(
              watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`)
            ) && (
              <div className="dropdown__filter-wrap__fields__filter dropdown__filter-wrap__fields__calendar">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    control={control}
                    rules={{
                      required: "required",
                    }}
                    name={`${controlName}.${indexSection}.conditions.${indexCondition}.value`}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <>
                          <label className="form-control-container__label">
                            Start Date
                            <DateField
                              disabled={
                                currentPathSplit?.[1] === "segments"
                                  ? true
                                  : isPendingDataValueDropdown && isFetchingDataValueDropdown
                              }
                              value={field.value?.[0] ? dayjs(field.value?.[0]) : null}
                              onChange={(e) => {
                                let currentValue = getValues(
                                  `${controlName}.${indexSection}.conditions.${indexCondition}.value`
                                );
                                let temp = Array.isArray(currentValue) ? [...currentValue] : [];
                                temp[0] =
                                  e && dayjs(e).isValid() ? dayjs(e).format("YYYY-MM-DD") : "";
                                // Only update start date, keep end date as is
                                field.onChange(temp);
                              }}
                              placeholder="Start Day"
                              maxDate={(function () {
                                const end = getValues(
                                  `${controlName}.${indexSection}.conditions.${indexCondition}.value`
                                )?.[1];
                                return end && dayjs(end).isValid() ? dayjs(end) : undefined;
                              })()}
                              onError={(newError) => {
                                if (newError === "maxDate") {
                                  setError(
                                    `${controlName}.${indexSection}.conditions.${indexCondition}.value`,
                                    {
                                      message: "Start date must be before end date",
                                    }
                                  );
                                } else {
                                  clearErrors(
                                    `${controlName}.${indexSection}.conditions.${indexCondition}.value`
                                  );
                                }
                              }}
                            />
                          </label>

                          {error?.message && (
                            <div className="create-segment__input-wrap__segment-err">
                              <img src={errorIcon} alt="Error Icon" />
                              {error?.message}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </LocalizationProvider>
              </div>
            )}

            {["between", "not between"]?.includes(
              watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`)
            ) &&
              watch(`${controlName}.${indexSection}.conditions.${indexCondition}.value`)?.length >
                0 && (
                <div className="dropdown__filter-wrap__fields__filter dropdown__filter-wrap__fields__calendar">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      control={control}
                      rules={{
                        required: "required",
                        validate: () => {
                          const start = getValues(
                            `${controlName}.${indexSection}.conditions.${indexCondition}.value`
                          )?.[0];
                          const end = getValues(
                            `${controlName}.${indexSection}.conditions.${indexCondition}.value`
                          )?.[1];
                          if (
                            start &&
                            end &&
                            dayjs(start).isValid() &&
                            dayjs(end).isValid()
                          ) {
                            return dayjs(end).isSame(dayjs(start)) ||
                              dayjs(end).isAfter(dayjs(start))
                              ? true
                              : "End date must be after start date";
                          }
                          return true;
                        },
                    }}
                    name={`${controlName}.${indexSection}.conditions.${indexCondition}.value`}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <>
                          <label className="form-control-container__label">
                            End Date
                            <DateField
                              disabled={
                                currentPathSplit?.[1] === "segments"
                                  ? true
                                  : isPendingDataValueDropdown && isFetchingDataValueDropdown
                              }
                              onChange={(e) => {
                                let currentValue = getValues(
                                    `${controlName}.${indexSection}.conditions.${indexCondition}.value`
                                  );
                                  let temp = Array.isArray(currentValue) ? [...currentValue] : [];
                                  temp[1] =
                                    e && dayjs(e).isValid() ? dayjs(e).format("YYYY-MM-DD") : "";
                                  // Only update end date, keep start date as is
                                  field.onChange(temp);
                                }}
                                value={field.value?.[1] ? dayjs(field.value?.[1]) : null}
                                placeholder="End Day"
                                minDate={(function () {
                                  const start = getValues(
                                    `${controlName}.${indexSection}.conditions.${indexCondition}.value`
                                  )?.[0];
                                  return start && dayjs(start).isValid() ? dayjs(start) : undefined;
                                })()}
                                onError={(newError) => {
                                  if (newError === "minDate") {
                                    setError(
                                      `${controlName}.${indexSection}.conditions.${indexCondition}.value`,
                                      {
                                        message: "End date must be after start date",
                                      }
                                    );
                                  } else {
                                    clearErrors(
                                      `${controlName}.${indexSection}.conditions.${indexCondition}.value`
                                    );
                                  }
                                }}
                              />
                            </label>

                            {(error?.message || !field?.value[1]) && (
                              <div className="create-segment__input-wrap__segment-err">
                                <img src={errorIcon} alt="Error Icon" />
                                {error?.message || "reqiured"}
                              </div>
                            )}
                          </>
                        );
                      }}
                    />
                  </LocalizationProvider>
                </div>
              )}

            {["in last", "in next"]?.includes(
              watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`)
            ) && (
              <div className="">
                <Controller
                  control={control}
                  rules={{
                    required: "required",
                  }}
                  name={`${controlName}.${indexSection}.conditions.${indexCondition}.value`}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <>
                        <label className="form-control-container__label">
                          Quantity Value
                          <TextField
                            type="number"
                            placeholder="Add number"
                            value={field.value?.[0] ?? ""}
                            onChange={(e) => {
                              let temp = Array.isArray(field.value) ? [...field.value] : [];
                              const val = parseInt(e.target.value, 10);

                              if (!isNaN(val) && val >= 1 && val <= 100) {
                                temp[0] = val;
                              } else if (e.target.value === "") {
                                temp[0] = "";
                              }

                              field.onChange(temp);
                            }}
                            slotProps={{
                              input: {
                                inputProps: {
                                  min: 1,
                                  max: 100,
                                },
                              },
                            }}
                          />
                        </label>

                        {(error?.message || !field?.value[0]) && (
                          <div className="create-segment__input-wrap__segment-err">
                            <img src={errorIcon} alt="Error Icon" />
                            {error?.message || "required"}
                          </div>
                        )}
                      </>
                    );
                  }}
                />
              </div>
            )}

            {["in last", "in next"]?.includes(
              watch(`${controlName}.${indexSection}.conditions.${indexCondition}.operator`)
            ) && (
              <div className="dropdown__filter-wrap__fields__filter">
                <Controller
                  control={control}
                  rules={{
                    required: "required",
                  }}
                  name={`${controlName}.${indexSection}.conditions.${indexCondition}.value`}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <>
                        <label className="form-control-container__label">
                          Time Period
                          <Select
                            {...field}
                            onChange={(e) => {
                              let temp =
                                getValues(
                                  `${controlName}.${indexSection}.conditions.${indexCondition}.value`
                                ) || [];

                              temp[1] = e.target.value;
                              field.onChange(temp);
                            }}
                            value={field.value?.[1] ?? ""}
                            className="form-control-container__select"
                          >
                            {["days", "months", "years"]?.map((option: any, index: number) => (
                              <MenuItem key={index} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </label>

                        {/* <CustomDateCheckbox
                          {...field}
                          label="Include current period"
                          className="create-campaign__field-btm-label"
                          checked={field.value?.[2] ?? true}
                          onChange={(e) => {
                            let temp =
                              getValues(
                                `${controlName}.${indexSection}.conditions.${indexCondition}.value`
                              ) || [];

                            temp[2] = e;

                            field.onChange(temp);
                          }}
                        /> */}

                        {(error?.message || !field?.value[1]) && (
                          <div className="create-segment__input-wrap__segment-err">
                            <img src={errorIcon} alt="Error Icon" />
                            {error?.message || "required"}
                          </div>
                        )}
                      </>
                    );
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterWrapSegmentSection;
